// ============================================================================
// personalityHelpers.js — MBTI personality type parsing & 16-color coding
// ============================================================================
// Fixed 16-colour palette based on MBTI types.
// These colours are for IDENTITY / VISUAL GROUPING only.
// They do NOT replace UI structural, brand, or project colours.
// Priority: UI > Brand > Project > Personality
// ============================================================================

// ── 16 MBTI TYPE COLORS ─────────────────────────────────────────────────────
// Within each group: similar hues (same family).
// Between groups: clearly different families.
// DO NOT modify these values. DO NOT generate dynamically.
export const MBTI_COLORS = {
  // ANALYSTS (NT) — Greens
  INTJ: '#49685a',
  INTP: '#537767',
  ENTJ: '#689581',
  ENTP: '#86aa9a',

  // DIPLOMATS (NF) — Reds / Clays
  INFJ: '#994b41',
  INFP: '#af564a',
  ENFJ: '#db6b5d',
  ENFP: '#e2897d',

  // SENTINELS (SJ) — Sage / Neutral
  ISTJ: '#828471',
  ISFJ: '#949681',
  ESTJ: '#b9bca1',
  ESFJ: '#d5d7c7',

  // EXPLORERS (SP) — Yellows / Oranges
  ISTP: '#a97e53',
  ISFP: '#c1905e',
  ESTP: '#f1b476',
  ESFP: '#f4c391',
};

// ── GROUP DEFINITIONS ────────────────────────────────────────────────────────
const PERSONALITY_GROUPS = {
  Analysts:  ['INTJ', 'INTP', 'ENTJ', 'ENTP'],
  Diplomats: ['INFJ', 'INFP', 'ENFJ', 'ENFP'],
  Sentinels: ['ISTJ', 'ISFJ', 'ESTJ', 'ESFJ'],
  Explorers: ['ISTP', 'ISFP', 'ESTP', 'ESFP'],
};

const NEUTRAL_HEX = '#94a3b8';

// ── RUNTIME OVERRIDES ────────────────────────────────────────────────────────
// Theme-configured MBTI colors override the defaults at runtime.
// Call setActiveMbtiColors() from useSettings when themeConfig loads.
let _activeMbtiColors = null;

export function setActiveMbtiColors(overrides) {
  _activeMbtiColors = overrides && typeof overrides === 'object' ? overrides : null;
}

function getActiveMbtiColor(type) {
  return (_activeMbtiColors && _activeMbtiColors[type]) || MBTI_COLORS[type];
}

// ── MBTI TYPE NAMES ──────────────────────────────────────────────────────────
const MBTI_TYPE_NAMES = {
  INTJ: 'Architect',    INTP: 'Logician',     ENTJ: 'Commander',   ENTP: 'Debater',
  INFJ: 'Advocate',     INFP: 'Mediator',     ENFJ: 'Protagonist', ENFP: 'Campaigner',
  ISTJ: 'Logistician',  ISFJ: 'Defender',     ESTJ: 'Executive',   ESFJ: 'Consul',
  ISTP: 'Virtuoso',     ISFP: 'Adventurer',   ESTP: 'Entrepreneur',ESFP: 'Entertainer',
};

// ── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Extract the base 4-letter MBTI type from a string.
 * Handles formats like:
 *   "INTJ", "INTJ-A", "INTJ-T"
 *   "Protagonist ENFJ-A", "Adventurer ISFP-T"
 * Searches anywhere in the string (not just the start).
 * Returns uppercase 4-letter type or null if invalid.
 */
export function parseMbtiType(value) {
  if (!value || typeof value !== 'string') return null;
  const match = value.trim().toUpperCase().match(/([IE][NS][TF][JP])/);
  return match ? match[1] : null;
}

/**
 * Get the personality type name (e.g., "Protagonist") from a value.
 * Tries to extract the name from the input string first (e.g., "Protagonist ENFJ-A" → "Protagonist"),
 * then falls back to the canonical MBTI type name lookup.
 * Returns null if no valid MBTI type is found.
 */
export function getPersonalityName(value) {
  const baseType = parseMbtiType(value);
  if (!baseType) return null;
  // Try to extract the name prefix from the input (before the MBTI code)
  if (value && typeof value === 'string') {
    const prefix = value.trim().replace(/[IE][NnSs][TtFf][JjPp][-]?[ATat]?$/i, '').trim();
    if (prefix && prefix.length > 0 && !/^[IE][NS][TF][JP]/i.test(prefix)) {
      return prefix;
    }
  }
  return MBTI_TYPE_NAMES[baseType] || null;
}

/**
 * Get the personality group name for a given personality value.
 * Returns 'Analysts', 'Diplomats', 'Sentinels', 'Explorers', or null.
 */
export function getPersonalityGroup(value) {
  const baseType = parseMbtiType(value);
  if (!baseType) return null;
  for (const [group, types] of Object.entries(PERSONALITY_GROUPS)) {
    if (types.includes(baseType)) return group;
  }
  return null;
}

/**
 * Returns black or white for readable text on a given hex background.
 */
export function getContrastColor(hex) {
  if (!hex || typeof hex !== 'string') return '#ffffff';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Relative luminance (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#1f2937' : '#ffffff';
}

/**
 * Get the hex color for a specific MBTI type.
 * Returns the per-type color from the 16-color palette, or neutral grey.
 */
export function getPersonalityHex(value) {
  const baseType = parseMbtiType(value);
  if (!baseType) return NEUTRAL_HEX;
  return getActiveMbtiColor(baseType) || NEUTRAL_HEX;
}

/**
 * Get color styling for a personality value.
 * Returns { bg, text, border, hex } — the hex is the solid per-type color.
 * bg is a 10% opacity version for subtle backgrounds.
 */
export function getPersonalityColor(value) {
  const hex = getPersonalityHex(value);
  if (hex === NEUTRAL_HEX) {
    return { bg: 'rgba(148, 163, 184, 0.12)', text: '#64748b', border: '#94a3b8', hex: NEUTRAL_HEX };
  }
  // Parse hex to RGB for the 10% opacity background
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return {
    bg: `rgba(${r}, ${g}, ${b}, 0.10)`,
    text: hex,
    border: hex,
    hex,
  };
}

/**
 * Get user initials from name string.
 * - "John Doe" => "JD"
 * - "Madonna" => "MA"
 * - null/empty => "?"
 */
export function getUserInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.trim().substring(0, 2).toUpperCase() || '?';
}

/**
 * Normalize an expertise array safely.
 * Ensures it is always a clean string array.
 */
export function normalizeExpertiseArray(value) {
  if (!value) return [];
  if (!Array.isArray(value)) return [];
  return value.filter(v => typeof v === 'string' && v.trim().length > 0).map(v => v.trim());
}
